import { DotLottie } from 'https://cdn.jsdelivr.net/npm/@lottiefiles/dotlottie-web@0.71.0/+esm';
import { getAxisAlignedBoundingRect } from './dotlottie-bounds.js';

const ZODIAC_RANGES = [
  { sign: 'capricorn', start: [1, 1], end: [1, 19] },
  { sign: 'aquarius', start: [1, 20], end: [2, 18] },
  { sign: 'pisces', start: [2, 19], end: [3, 20] },
  { sign: 'aries', start: [3, 21], end: [4, 19] },
  { sign: 'taurus', start: [4, 20], end: [5, 20] },
  { sign: 'gemini', start: [5, 21], end: [6, 20] },
  { sign: 'cancer', start: [6, 21], end: [7, 22] },
  { sign: 'leo', start: [7, 23], end: [8, 22] },
  { sign: 'virgo', start: [8, 23], end: [9, 22] },
  { sign: 'libra', start: [9, 23], end: [10, 22] },
  { sign: 'scorpio', start: [10, 23], end: [11, 21] },
  { sign: 'sagittarius', start: [11, 22], end: [12, 21] },
  { sign: 'capricorn', start: [12, 22], end: [12, 31] },
];

function birthdayToZodiac(month, day) {
  const m = parseInt(month, 10);
  const d = parseInt(day, 10);
  for (const { sign, start, end } of ZODIAC_RANGES) {
    const afterStart = m > start[0] || (m === start[0] && d >= start[1]);
    const beforeEnd = m < end[0] || (m === end[0] && d <= end[1]);
    if (afterStart && beforeEnd) return sign;
  }
  return 'capricorn';
}

class QuizIntro extends HTMLElement {
  connectedCallback() {
    const src = this.dataset.src;
    const teapotSrc = this.dataset.teapotSrc || '';
    const loadingSrc = this.dataset.loadingSrc || '';
    const resultUrl = this.dataset.resultUrl || '/pages/test-result';
    const startSound = this.dataset.startSound || '';

    if (!src) return;
    if (startSound) {
      this._startAudio = new Audio(startSound);
      this._startAudio.volume = 0.15;
      this._startAudio.preload = 'auto';
    }
    this._resultUrl = resultUrl;
    this._loadingSrc = loadingSrc;
    this._teapotSrc = teapotSrc;
    this._init(src);
  }

  disconnectedCallback() {
    this._resizeObserver?.disconnect();
    this._dotLottie?.destroy();
    this._teapotLottie?.destroy();
    this._loadingLottie?.destroy();
    this._startAudio?.pause();
  }

  _init(src) {
    const canvas = this.querySelector('.quiz-intro__player');
    if (!canvas) return;

    const resizeCanvas = () => {
      const w = canvas.clientWidth * window.devicePixelRatio;
      const h = canvas.clientHeight * window.devicePixelRatio;
      if (canvas.width === w && canvas.height === h) return;
      canvas.width = w;
      canvas.height = h;
    };
    resizeCanvas();

    this._dotLottie = new DotLottie({
      canvas,
      src,
      autoplay: true,
      loop: false,
      layout: { fit: 'contain', align: [0.5, 0.5] },
      renderConfig: {
        devicePixelRatio: window.devicePixelRatio,
        useFrameInterpolation: false,
        freezeOnOffscreen: true,
      },
    });

    this._dotLottie.addEventListener('load', () => {
      const totalFrames = this._dotLottie.totalFrames;
      const markers = this._dotLottie.markers() || [];
      const loopMarker = markers.find(m => m.name === 'loop-start');
      this._loopStart = loopMarker ? loopMarker.time : 0;
      this._triggerFrame = Math.floor(totalFrames * 0.99);
      this._positionFields();
    });

    this.dataset.stage = 'intro';

    this._dotLottie.addEventListener('frame', ({ currentFrame }) => {
      if (this._triggerFrame == null) return;
      if (currentFrame >= this._triggerFrame) {
        this._dotLottie.setFrame(this._loopStart);
        this._dotLottie.play();
      }
    });

    const btn = this.querySelector('.quiz-intro__start');
    if (btn) btn.addEventListener('click', () => this._onStart());

    for (const selector of ['.quiz-intro__month', '.quiz-intro__day']) {
      const sel = this.querySelector(selector);
      if (sel) sel.addEventListener('change', () => {
        if (sel.value) sel.style.backgroundImage = 'none';
      });
    }

    this._resizeObserver = new ResizeObserver(() => {
      resizeCanvas();
      this._dotLottie?.resize();
      this._positionFields();

      const teapotCanvas = this.querySelector('.quiz-intro__teapot-player');
      if (teapotCanvas && teapotCanvas.style.display !== 'none') {
        teapotCanvas.width = teapotCanvas.clientWidth * window.devicePixelRatio;
        teapotCanvas.height = teapotCanvas.clientHeight * window.devicePixelRatio;
        this._teapotLottie?.resize();
      }
    });
    this._resizeObserver.observe(canvas);
  }

  _onStart() {
    const name = this.querySelector('.quiz-intro__name')?.value?.trim();
    const month = this.querySelector('.quiz-intro__month')?.value;
    const day = this.querySelector('.quiz-intro__day')?.value;

    if (!name || !month || !day) return;

    const date = new Date(2000, parseInt(month, 10) - 1, parseInt(day, 10));
    if (date.getMonth() !== parseInt(month, 10) - 1) return;

    if (this._startAudio) {
      this._startAudio.currentTime = 0;
      this._startAudio.play().catch(() => { });
    }

    this._name = name;
    this._month = month;
    this._day = day;
    this._submitted = false;

    const btn = this.querySelector('.quiz-intro__start');
    if (btn) btn.style.display = 'none';

    if (this._teapotSrc) {
      this._showTeapot(this._teapotSrc);
    } else {
      this._showLoading();
    }
  }

  _showTeapot(src) {
    this.dataset.stage = 'teapot';
    const teapotCanvas = this.querySelector('.quiz-intro__teapot-player');
    if (!teapotCanvas) { this._showLoading(); return; }

    teapotCanvas.width = teapotCanvas.clientWidth * window.devicePixelRatio;
    teapotCanvas.height = teapotCanvas.clientHeight * window.devicePixelRatio;

    teapotCanvas.style.opacity = '0';
    teapotCanvas.style.display = 'block';

    this._teapotLottie = new DotLottie({
      canvas: teapotCanvas,
      src,
      autoplay: true,
      loop: false,
      layout: { fit: 'contain', align: [0.5, 0.5] },
      renderConfig: {
        devicePixelRatio: window.devicePixelRatio * 0.75,
        useFrameInterpolation: false,
      },
    });

    requestAnimationFrame(() => {
      teapotCanvas.style.transition = 'opacity 0.4s ease';
      teapotCanvas.style.opacity = '1';
    });

    this._teapotLottie.addEventListener('frame', ({ currentFrame }) => {
      if (!this._submitted && currentFrame >= 160) {
        this._submitted = true;
        this._teapotLottie.pause();
        this._showLoading();
      }
    });
    this._teapotLottie.addEventListener('loadError', () => this._showLoading());
  }

  _showLoading() {
    this.dataset.stage = 'loading';
    const loadingCanvas = this.querySelector('.quiz-intro__loading-player');
    if (!loadingCanvas || !this._loadingSrc) {
      window.location.href = this._buildResultUrl();
      return;
    }

    loadingCanvas.width = loadingCanvas.clientWidth * window.devicePixelRatio;
    loadingCanvas.height = loadingCanvas.clientHeight * window.devicePixelRatio;
    loadingCanvas.style.display = 'block';

    this._loadingLottie = new DotLottie({
      canvas: loadingCanvas,
      src: this._loadingSrc,
      autoplay: true,
      loop: true,
      layout: { fit: 'contain', align: [0.5, 0.5] },
      renderConfig: {
        devicePixelRatio: window.devicePixelRatio * 0.75,
        useFrameInterpolation: false,
      },
    });

    let loadingTriggerFrame = null;
    let playCount = 0;

    this._loadingLottie.addEventListener('load', () => {
      loadingTriggerFrame = Math.floor(this._loadingLottie.totalFrames * 0.90);
    });

    this._loadingLottie.addEventListener('frame', ({ currentFrame }) => {
      if (loadingTriggerFrame == null) return;
      if (currentFrame >= loadingTriggerFrame) {
        playCount++;
        if (playCount >= 2) {
          window.location.href = this._buildResultUrl();
        } else {
          const fps = this._loadingLottie.totalFrames / this._loadingLottie.duration;
          // this._loadingLottie.setFrame(Math.floor(fps * 5));
          this._loadingLottie.play();
        }
      }
    });

    this._loadingLottie.addEventListener('loadError', () => {
      window.location.href = this._buildResultUrl();
    });
  }

  _positionFields() {
    const dpr = window.devicePixelRatio;
    let allFound = true;

    for (const [layerName, selector] of [
      ['name-0.png', '.quiz-intro__name'],
      ['mm-0.png', '.quiz-intro__month'],
      ['dd-0.png', '.quiz-intro__day'],
    ]) {
      const el = this.querySelector(selector);
      if (!el) continue;
      const rect = getAxisAlignedBoundingRect(this._dotLottie?.getLayerBoundingBox(layerName));
      if (!rect) { allFound = false; continue; }
      el.style.left = `${rect.x / dpr}px`;
      el.style.top = `${rect.y / dpr}px`;
      el.style.width = `${rect.width / dpr}px`;
      el.style.height = `${rect.height / dpr}px`;
    }

    return allFound;
  }

  _buildResultUrl() {
    const zodiac = birthdayToZodiac(this._month, this._day);
    const url = new URL(this._resultUrl, window.location.origin);
    url.searchParams.set('result', zodiac);
    url.searchParams.set('name', this._name);
    return url.toString();
  }
}

customElements.define('quiz-intro', QuizIntro);
